import { FC, useContext, useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import Lightbox from "react-image-lightbox";
import { useUnmount } from "react-use";
import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

import { PropsWithElementAttributes } from "./utils";
import { AttachmentDetail } from "interfaces/attachment";
import { downloadAsDataUri } from "core/utils/aws/downloadAsDataUri";
import {
  getAttachment,
  getAttachmentsByCaseId
} from "core/utils/aws/getAttachment";
import { AxiosContext } from "contexts/AxiosContext";
import { downloadFile } from "core/utils/aws/downloadFile";

export type ThumbSize = "small" | "medium" | "large";

export type GalleryProps = PropsWithElementAttributes<{
  eventId: number;
  thumbSize?: ThumbSize;
  expandedSize?: ThumbSize | "original";
  images?: AttachmentDetail[];
}>;

export const Gallery: FC<GalleryProps> = ({
  eventId,
  className,
  style,
  thumbSize = "medium",
  images = []
}): JSX.Element => {
  const { axios } = useContext(AxiosContext)!;
  const [expandIndex, setExpandIndex] = useState<number | null>(null);
  const [attachmentsList, setAttachmentsList] = useState<AttachmentDetail[]>(
    []
  );
  const topFiveImages = useMemo(() => images.slice(0, 5), [images]);
  const [downloadedGalleryImages, setDownloadedGalleryImages] = useState<
    PromiseSettledResult<string>[]
  >([]);
  const [downloadedLightboxImages, setDownloadedLightboxImages] = useState<
    PromiseSettledResult<string>[]
  >([]);
  const [, setIsLoadingGalleryImages] = useState(true);
  const [, setIsErrorGalleryImages] = useState(false);
  const [, setIsLoadingLightboxImages] = useState(true);
  const [, setIsErrorLightboxImages] = useState(false);

  const mergedStyles = useMemo(
    () => ({
      height: images.length > 3 ? "400px" : "200px",
      ...style
    }),
    [images, style]
  );

  const controller = new AbortController();
  useUnmount(() => controller.abort());

  useEffect(() => {
    (async () => {
      const downloads: Promise<string>[] = [];

      for (const image of topFiveImages) {
        let { url, download_headers: headers } = image.original_file;

        if (image[`thumbnail_${thumbSize}`]) {
          url = image[`thumbnail_${thumbSize}`]!.url;
          headers = image[`thumbnail_${thumbSize}`]!.download_headers;
        }

        try {
          downloads.push(
            downloadAsDataUri({
              url,
              headers,
              signal: controller.signal
            })
          );
        } catch (err) {
          console.info(err);
          setIsErrorGalleryImages(true);
        }
      }

      const results = await Promise.allSettled<string>(downloads);
      setDownloadedGalleryImages(results);

      setIsLoadingGalleryImages(false);
    })();
    // exclude controller.signal from deps as it causes a memory leak
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thumbSize, topFiveImages]);

  const handleExpand = (index: number): void => {
    setExpandIndex(index);

    (async () => {
      try {
        const attachments = await getAttachmentsByCaseId(axios, eventId);
        setAttachmentsList(attachments);

        const downloads: Promise<string>[] = [];

        for (const image of attachments) {
          if (!image.original_file.type.startsWith("image")) {
            continue;
          }

          let url = image.original_file.url;
          let headers = image.original_file.download_headers;

          if (image["thumbnail_large"]) {
            url = image.thumbnail_large?.url ?? url;
            headers = image.thumbnail_large?.download_headers ?? headers;
          }

          downloads.push(
            downloadAsDataUri({
              url,
              headers,
              signal: controller.signal
            })
          );
        }

        const results = await Promise.allSettled<string>(downloads);
        setDownloadedLightboxImages(results);
      } catch (err) {
        console.info(err);
        setIsErrorLightboxImages(true);
      } finally {
        setIsLoadingLightboxImages(false);
      }
    })();
  };

  return (
    <Box className={classNames("raf-gallery", className)} style={mergedStyles}>
      {downloadedGalleryImages.map((image, index) => (
        <StyledButton
          className={classNames("img", {
            "img--last": index === 4 && images.length > 5
          })}
          onClick={() => handleExpand(index)}
          key={`image-${index}`}
          aria-label={images[index]?.original_file?.name ?? ""}
        >
          <img
            src={(image as PromiseFulfilledResult<string>).value}
            className="raf-gallery__image"
            style={{ pointerEvents: "none" }}
            alt=""
          />
          {index === 4 && images.length > 5 && <p>{images.length - 4} more</p>}
        </StyledButton>
      ))}

      {expandIndex !== null && (
        <Lightbox
          mainSrc={
            (downloadedLightboxImages[expandIndex] as PromiseFulfilledResult<
              string
            >)?.value ?? ""
          }
          nextSrc={
            (downloadedLightboxImages[
              expandIndex + 1
            ] as PromiseFulfilledResult<string>)?.value ?? ""
          }
          prevSrc={
            (downloadedLightboxImages[
              expandIndex - 1
            ] as PromiseFulfilledResult<string>)?.value ?? ""
          }
          mainSrcThumbnail={
            (downloadedGalleryImages[expandIndex] as PromiseFulfilledResult<
              string
            >)?.value ?? ""
          }
          prevSrcThumbnail={
            (downloadedGalleryImages[expandIndex + 1] as PromiseFulfilledResult<
              string
            >)?.value ?? ""
          }
          nextSrcThumbnail={
            (downloadedGalleryImages[expandIndex - 1] as PromiseFulfilledResult<
              string
            >)?.value ?? ""
          }
          onCloseRequest={() => setExpandIndex(null)}
          onMoveNextRequest={() => setExpandIndex(expandIndex + 1)}
          onMovePrevRequest={() => setExpandIndex(expandIndex - 1)}
          reactModalStyle={{
            overlay: { zIndex: 1500 },
            container: { zIndex: 1500 }
          }}
          toolbarButtons={[
            <StyledToolbarButton
              className="ril-close ril-toolbar__item__child ril__toolbarItemChild ril__builtinButton"
              onClick={async () => {
                const attachmentId = attachmentsList.filter(attachment =>
                  attachment.original_file.type.startsWith("image")
                )[expandIndex].id;

                const refetchData = await getAttachment(axios, attachmentId);

                downloadFile({
                  url: refetchData.original_file.url,
                  headers: refetchData.original_file.download_headers,
                  signal: controller.signal,
                  filename: refetchData.original_file.name
                });
              }}
            >
              <FileDownloadIcon />
            </StyledToolbarButton>
          ]}
        />
      )}
    </Box>
  );
};

const StyledButton = styled("button")`
  appearance: "none";
  border: 0;
  padding: 0;
  outline-offset: -1px;
  cursor: pointer;

  :focus-visible {
    outline: 1px solid ${({ theme }) => theme.palette.primary.main};
  }
`;

const StyledToolbarButton = styled("button")`
  appearance: "none";
  background: transparent;
  border: 0;
  padding: 0;
  outline-offset: -1px;
  cursor: pointer;

  svg {
    margin-top: 5px;
    fill: white;
  }
`;
